import React, { useEffect, useState, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate, useLocation, useSearchParams } from "react-router-dom";
import { Auth } from "aws-amplify";
import { getPageContent } from "../../utils";
import { useAuth } from "../../hooks/useAuth";
import {
    selectApplicantDetailsData,
    selectApplicantDetailsStatus,
    selectApplicantDetailsError,
    initializeAsync,
} from "./applicantDetailsSlice";

import { get, post } from "../../api";

// Add custom imports here

import {
    Button,
    CssBaseline,
    Box,
    Paper,
    Menu,
    MenuItem,
    Container,
    Typography,
    Card,
    CardContent,
    CardHeader,
    Divider,
    Backdrop,
    CircularProgress,
    Alert,
    AlertTitle,
    Snackbar,
} from "@mui/material";

import { Input } from "../../components/Input";
import LoadingButton from "@mui/lab/LoadingButton";
import { updateApplicant } from "../../utils";

import { init, FullStory } from "@fullstory/browser";
init({ orgId: process.env.REACT_APP_FULL_STORY_ORG_ID });

const ApplicantDetails = () => {
    const data = useSelector(selectApplicantDetailsData);
    const status = useSelector(selectApplicantDetailsStatus);
    const error = useSelector(selectApplicantDetailsError);
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    const [searchParams, setSearchParams] = useSearchParams();
    const { passwordLessLogin, login } = useAuth();
    const [openToster, setOpenToster] = useState(false);
    const [tosterVariant, setTosterVariant] = useState("error");
    const [tosterMessage, setTosterMessage] = useState("");
    const [loading, setLoading] = useState(false);
    const [details, setDetails] = useState({
        firstName:  "",
        lastName: "",
        email: location?.state?.searchParams?.email
    });

    const firstNameRef = useRef();
    const lastNameRef = useRef();
    const phoneRef = useRef();
    const marketRef = useRef();
    const moveInDateRef = useRef();

    const refs = [firstNameRef, lastNameRef, phoneRef, marketRef, moveInDateRef];

    // Add Variables

    useEffect(() => {
        if (status === "idle") {
            dispatch(initializeAsync());
        }
    }, [status, dispatch]);

    const handleChange = (key, value) => {
        let data = { ...details };
        data[key] = value;

        setDetails(data);
    };

    const handleTosterClose = () => {
        setOpenToster(false);
    };

    const handleClick = async () => {
        if (validate()) {
            setLoading(true);
            let record = { ...details };
            post(`lead/createLead`, record).then(async (result) => {
                if (result.isSuccess && result.data === true) {
                    passwordLessLogin({
                        email: record.email,
                        callback: (failed) => {
                            if (failed) {
                                setTosterVariant("error");
                                setTosterMessage(`Sorry, we don't recognize this email address`);
                                setOpenToster(true);
                            }
                            setLoading(false);
                        },
                    });
                } else {
                    setTosterVariant("error");
                    setTosterMessage(`Some error occur, please contact to the admin.`);
                    setOpenToster(true);
                    setLoading(false);
                }
                
            });
    
        }
        
        
    };

    const validate = () => {
        let hasError = false;
        for (let i = 0; i < refs.length; i++) {
            if (!refs[i].current.validate()) {
                hasError = true;
            }
        }
        return !hasError;
    };

    const getMarketOption = () => {
        return [
            {
                value: "Atlanta",
                label: "Atlanta",
            },
            {
                value: "Carolinas",
                label: "Carolinas",
            },
            {
                value: "Dallas",
                label: "Dallas",
            },
            {
                value: "Orlando",
                label: "Orlando",
            },
            {
                value: "Phoenix",
                label: "Phoenix",
            },
            {
                value: "Tampa",
                label: "Tampa",
            },
            {
                value: "Other",
                label: "Other",
            },
        ]
    }

    const getMoveInTimeOption = () => {
        return [
            {
                value: "ASAP",
                label: "ASAP",
            },
            {
                value: "1 month",
                label: "1 month",
            },
            {
                value: "2-3 Months",
                label: "2-3 Months",
            },
            {
                value: "4+ Months",
                label: "4+ Months",
            },
            {
                value: "Not sure",
                label: "Not sure",
            }
        ]
    }

    let body = (
        <Box
            component="main"
            sx={{
                position: "absolute",
                width: "100%",
                height: "100%",
            }}
        >
            <Box
                sx={{
                    alignItems: "center",
                    display: "flex",
                    flexGrow: 1,
                    minHeight: "100%",
                }}
            >
                <Container maxWidth="sm">
                    <CssBaseline />
                    <Paper
                        elevation={16}
                        sx={{
                            backgroundColor: "rgb(255, 255, 255)",
                            color: "rgb(18, 24, 40)",
                            transition: "box-shadow 300ms cubic-bezier(0.4, 0, 0.2, 1) 0ms",
                            borderRadius: "8px",
                            boxShadow: "rgb(100 116 139 / 12%) 0px 10px 15px",
                            backgroundImage: "none",
                            overflow: "hidden",
                            padding: "32px",
                            width: "100%",
                        }}
                    >
                        <Box sx={{ textAlign: "center" }}>
                            <Typography component="h1" variant="h4">
                                Sign up!
                            </Typography>
                            <Typography
                                align="center"
                                color="textSecondary"
                                variant="body1"
                                fontSize="0.875rem"
                                marginTop="1rem"
                            >
                                Let's create an account to start your application.
                            </Typography>
                        </Box>
                        <Box validate sx={{ mt: 1 }}>
                            <Input
                                id="firstName"
                                label="First name"
                                name="firstName"
                                margin="normal"
                                isRequired={true}
                                value={details.firstName}
                                autoFocus
                                ref={firstNameRef}
                                fullWidth
                                autoComplete="off"
                                onChange={(value) => handleChange("firstName", value)}
                            />

                            <Input
                                id="lastName"
                                label="Last name"
                                name="lastName"
                                margin="normal"
                                ref={lastNameRef}
                                isRequired={true}
                                value={details.lastName}
                                autoFocus
                                fullWidth
                                autoComplete="off"
                                onChange={(value) => handleChange("lastName", value)}
                            />

                            <Input
                                id="phone"
                                type="maskType"
                                margin="normal"
                                ref={phoneRef}
                                label={"Phone Number"}
                                autoComplete="off"
                                onChange={(value) => handleChange("phone", value)}
                                mask={"(999) 999-9999"}
                                isRequired={true}
                            ></Input>

                            <Input
                                id="market"
                                type="select"
                                margin="normal"
                                ref={marketRef}
                                label={"Market"}
                                options={getMarketOption()}
                                autoComplete="off"
                                onChange={(value) => handleChange("market", value)}
                                isRequired={true}
                            ></Input>

                            <Input
                                id="moveInDate"
                                type="select"
                                margin="normal"
                                ref={moveInDateRef}
                                label={"What is your timeframe to move-in?"}
                                options={getMoveInTimeOption()}
                                autoComplete="off"
                                onChange={(value) => handleChange("moveInDate", value)}
                                isRequired={true}
                            ></Input>

                            <LoadingButton
                                size="small"
                                type="submit"
                                color="primary"
                                disabled={details?.firstName && details?.lastName && details?.phone && details?.market && details?.moveInDate ? false : true}
                                fullWidth
                                loading={loading}
                                loadingPosition="end"
                                variant="contained"
                                onClick={handleClick}
                                sx={{ mt: 3, mb: 2, height: "45px" }}
                            >
                                Submit
                            </LoadingButton>
                        </Box>
                    </Paper>
                </Container>
            </Box>
            <Snackbar
                anchorOrigin={{ vertical: "top", horizontal: "center" }}
                open={openToster}
                autoHideDuration={6000}
                onClose={handleTosterClose}
            >
                <Alert onClose={handleTosterClose} severity={tosterVariant} variant="filled">
                    {tosterMessage}
                </Alert>
            </Snackbar>
        </Box>
    );

    return getPageContent(body, status, error);
};

export { ApplicantDetails };
